import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Link2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import type {
  DesignChange,
  FailureMode,
  FailureModeRevisionPreview,
  UpdateFailureModeReviewRequest,
} from '../../types/drbfm';
import EvidenceRefList from '../evidence/EvidenceRefList';
import { useFailureModeEvidence } from '../evidence/useFailureModeEvidence';
import DecisionBasisCard from './decision-memory/DecisionBasisCard';
import DecisionMemoryHeader from './decision-memory/DecisionMemoryHeader';
import DrawingEvidenceSection from './decision-memory/DrawingEvidenceSection';
import FailureModeRevisionComposer from './decision-memory/FailureModeRevisionComposer';
import KnowledgeSummaryList from './decision-memory/KnowledgeSummaryList';
import ReviewQuestionList from './decision-memory/ReviewQuestionList';
import UsedPrecheckAnswerList from './decision-memory/UsedPrecheckAnswerList';
import { getSeverityLabel } from './decision-memory/decisionMemoryText';

interface DRBFMDecisionMemoryPanelProps {
  failureMode: FailureMode | null;
  designChange?: DesignChange;
  isReviewing?: boolean;
  onPreviewRevision?: (instruction: string) => Promise<FailureModeRevisionPreview>;
  onReview?: (request: UpdateFailureModeReviewRequest) => Promise<void>;
  onOpenDrawingEvidence: (failureMode: FailureMode) => void;
  onClose?: () => void;
}

const DRBFMDecisionMemoryPanel: React.FC<DRBFMDecisionMemoryPanelProps> = ({
  failureMode,
  designChange,
  isReviewing = false,
  onPreviewRevision,
  onReview,
  onOpenDrawingEvidence,
  onClose,
}) => {
  const { t } = useTranslation();
  const evidence = useFailureModeEvidence(failureMode);

  if (!failureMode) {
    return (
      <div className="rounded-xl border bg-card p-4 text-card-foreground">
        <DecisionMemoryHeader onClose={onClose} />
        <Alert variant="info">
          <Info className="size-4" strokeWidth={1.9} />
          <AlertDescription className="revy-cjk leading-[1.6]">
            {t('drbfm.decisionMemory.selectFailureMode', '故障モードを選択してください。')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const hasKnowledge = failureMode.knowledgeReferences.length > 0;
  const hasQuestions = Boolean(failureMode.reviewQuestions?.length);
  const hasUsedAnswers = Boolean(failureMode.usedPrecheckAnswers?.length);
  const hasUnknownAssumptions = Boolean(failureMode.unknownAssumptions?.length);

  return (
    <div className="sticky top-4 rounded-xl border bg-card p-4 text-card-foreground">
      <div className="flex flex-col gap-4">
        <div>
          <DecisionMemoryHeader onClose={onClose} />
          <p className="revy-cjk text-sm font-medium leading-[1.6]">
            {failureMode.failureDescription}
          </p>
          {failureMode.failureCause && (
            <p className="revy-cjk mt-1 text-sm leading-[1.6] text-muted-foreground">
              {failureMode.failureCause}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Badge variant="warning" className="revy-cjk">
            {t('drbfm.decisionMemory.severity', {
              value: getSeverityLabel(failureMode),
              defaultValue: `重大度 ${getSeverityLabel(failureMode)}`,
            })}
          </Badge>
          <Badge variant="destructive" className="revy-figure">
            {`RPN ${failureMode.rpnScore || '-'}`}
          </Badge>
          {hasKnowledge && (
            <Badge variant="secondary" className="revy-cjk gap-1">
              <Link2 className="size-3" strokeWidth={2} />
              {t('drbfm.decisionMemory.evidenceAvailable', '根拠あり')}
            </Badge>
          )}
          {failureMode.drawingEvidence && (
            <Badge variant="info" className="revy-cjk">
              {t('drbfm.decisionMemory.drawingLinked', '図面リンク済み')}
            </Badge>
          )}
          {hasQuestions && (
            <Badge variant="secondary" className="revy-cjk">
              {t('drbfm.decisionMemory.questionsGenerated', '質問生成')}
            </Badge>
          )}
        </div>

        <DecisionBasisCard failureMode={failureMode} designChange={designChange} />

        {onPreviewRevision && onReview && (
          <FailureModeRevisionComposer
            failureMode={failureMode}
            isBusy={isReviewing}
            onPreview={onPreviewRevision}
            onReview={onReview}
          />
        )}

        {hasQuestions && <ReviewQuestionList questions={failureMode.reviewQuestions!} />}

        {hasUsedAnswers && <UsedPrecheckAnswerList answers={failureMode.usedPrecheckAnswers!} />}

        {hasUnknownAssumptions && (
          <Alert variant="warning" className="py-2">
            <AlertTitle className="revy-cjk mb-1 leading-[1.5]">
              {t('drbfm.decisionMemory.unresolvedAssumptions', '未確定の前提')}
            </AlertTitle>
            <ul className="list-disc pl-4">
              {failureMode.unknownAssumptions!.map((assumption, index) => (
                <li key={`${assumption}-${index}`} className="revy-cjk text-sm leading-[1.6]">
                  {assumption}
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {hasKnowledge && <KnowledgeSummaryList references={failureMode.knowledgeReferences} />}

        {failureMode.drawingEvidence && (
          <DrawingEvidenceSection
            evidence={failureMode.drawingEvidence}
            onOpen={() => onOpenDrawingEvidence(failureMode)}
          />
        )}

        {evidence.revisionContextMissing && (
          <Alert variant="warning" className="py-2">
            <AlertDescription className="revy-cjk leading-[1.6]">
              {t('evidence.missingRevisionContext')}
            </AlertDescription>
          </Alert>
        )}
        {evidence.retrievalTimeMissing && (
          <Alert variant="warning" className="py-2">
            <AlertDescription className="revy-cjk leading-[1.6]">
              {t('evidence.missingRetrievalTime')}
            </AlertDescription>
          </Alert>
        )}

        {/* The section is absent when the deployment has the evidence adapters switched
            off — a configuration state, not a failure to report to the reviewer. */}
        {!evidence.revisionContextMissing && !evidence.retrievalTimeMissing && !evidence.featureUnavailable && (
          <EvidenceRefList
            finding={evidence.finding}
            notes={evidence.notes}
            error={evidence.error}
            errorKind={evidence.errorKind ?? undefined}
            loading={evidence.loading}
          />
        )}
      </div>
    </div>
  );
};

export default DRBFMDecisionMemoryPanel;
